import React, { useState, useEffect, useRef } from 'react';
import { Trash2, Menu, Search, X, Play, Pause } from 'lucide-react';
import { collection, query, where, orderBy, getDocs, addDoc, updateDoc, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { db, storage, ref, deleteObject } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useMobileMenu } from '../contexts/MobileMenuContext';
import { useUser } from '../contexts/UserContext';
import { Capacitor } from '@capacitor/core';
import DeleteModal from './modals/DeleteModal';
import UpgradeButton from './common/UpgradeButton';
import AudioRecorder from './write/AudioRecorder';
import { getTotalCreationsCount, incrementCreationCount, FREE_TIER_LIMIT } from '../lib/userLimits';
import { vibrate, HapticPatterns } from '../utils/haptics';

interface Idea {
  id: string;
  title: string;
  description: string;
  tags: string[];
  createdAt: string;
  userId: string;
  audioUrl?: string;
  updatedAt?: string;
}

export default function IdeaBank() {
  const { user } = useAuth();
  const { isMainMenuOpen, setIsMainMenuOpen } = useMobileMenu();
  const { isPremium, isAdmin } = useUser();
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [newIdea, setNewIdea] = useState({
    title: '',
    description: '',
    tags: [] as string[],
    audioUrl: '',
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newTag, setNewTag] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'title'>('date');
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; id: string; title: string }>({
    isOpen: false,
    id: '',
    title: ''
  });
  const [totalIdeasCreated, setTotalIdeasCreated] = useState(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [playingIdeaId, setPlayingIdeaId] = useState<string | null>(null);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);

  // Fetch user's ideas from Firestore
  useEffect(() => {
    if (!user) return;

    const fetchIdeas = async () => {
      try {
        const q = query(
          collection(db, 'ideas'),
          where('userId', '==', user.uid),
          orderBy('createdAt', 'desc')
        );
        console.log('Fetching ideas for user:', user.uid);
        const snapshot = await getDocs(q);
        const ideasData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Idea[];
        console.log('Fetched ideas:', ideasData);
        setIdeas(ideasData);
        setLoading(false);

        // Get total ideas created
        const count = await getTotalCreationsCount(user.uid, 'ideas');
        setTotalIdeasCreated(count);
      } catch (err) {
        console.error('Error fetching ideas:', err);
        setError(`Unable to load ideas: ${(err as Error).message}`);
        setLoading(false);
      }
    };

    fetchIdeas();
  }, [user]);

  const handleSaveIdea = async (e?: React.FormEvent) => {
    vibrate(HapticPatterns.MEDIUM);
    if (e) {
      e.preventDefault();
    }
    
    if (!user || !newIdea.title.trim()) return;

    try {
      // Check for total idea limit for free users
      if (!isPremium && !isAdmin && totalIdeasCreated >= FREE_TIER_LIMIT) {
        setError(Capacitor.getPlatform() === 'ios' ? "You've reached the maximum allowed ideas for your account. To manage your account, visit www.showyourbits.com" : 'Free users can create up to 10 ideas total. Upgrade to Premium for unlimited ideas!');
        return;
      }

      if (newIdea.description.length > 500 && !isPremium && !isAdmin) {
        setError(Capacitor.getPlatform() === 'ios' ? "You've reached the maximum allowed characters for your idea. To manage your account, visit www.showyourbits.com" : 'Free users are limited to 500 characters per idea. Upgrade to Premium for unlimited characters!');
        return;
      }

      const ideaData = {
        title: newIdea.title,
        description: newIdea.description,
        audioUrl: newIdea.audioUrl || '',
        tags: newIdea.tags || [],
        userId: user.uid,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      console.log('Saving idea with data:', ideaData);

      // Save to Firestore
      const docRef = await addDoc(collection(db, 'ideas'), ideaData);
      console.log('Saved idea with ID:', docRef.id);
      
      // Increment total ideas created
      await incrementCreationCount(user.uid, 'ideas');
      setTotalIdeasCreated(prev => prev + 1);
      
      // Update local state
      setIdeas(prev => [{
        id: docRef.id,
        ...ideaData
      }, ...prev]);

      // Reset form
      setNewIdea({
        title: '',
        description: '',
        tags: [],
        audioUrl: ''
      });
      setError(null);
    } catch (err) {
      console.error('Error saving idea:', err);
      setError('Failed to save idea. Please try again.');
    }
  };

  const handleUpdateIdea = async (id: string, updates: Partial<Idea>) => {
    vibrate(HapticPatterns.MEDIUM);
    if (!user) return;

    try {
      if (updates.description && updates.description.length > 500 && !isPremium && !isAdmin) {
        setError(Capacitor.getPlatform() === 'ios' ? "You've reached the maximum allowed characters for your idea. To manage your account, visit www.showyourbits.com" : 'Free users are limited to 500 characters per idea. Upgrade to Premium for unlimited characters!');
        return;
      }

      await updateDoc(doc(db, 'ideas', id), updates);
      setIdeas(prev => prev.map(idea => 
        idea.id === id ? { ...idea, ...updates } : idea
      ));
      setEditingId(null);
    } catch (err) {
      console.error('Error updating idea:', err);
      setError('Failed to update idea');
    }
  };

  const handleDeleteIdea = async (id: string) => {
    vibrate(HapticPatterns.ERROR);
    if (!user) return;

    try {
      // First, get the idea to access its audioUrl
      const ideaRef = doc(db, 'ideas', id);
      const ideaDoc = await getDoc(ideaRef);
      
      if (!ideaDoc.exists()) {
        console.error('Idea not found');
        return;
      }

      const ideaData = ideaDoc.data();
      
      // If there's an audio file, delete it from storage first
      if (ideaData?.audioUrl) {
        try {
          const audioRef = ref(storage, ideaData.audioUrl);
          await deleteObject(audioRef);
          console.log('Successfully deleted audio file');
        } catch (storageErr) {
          console.error('Error deleting audio file:', storageErr);
          // Continue with document deletion even if storage deletion fails
        }
      }

      // Then delete the document
      await deleteDoc(ideaRef);
      console.log('Successfully deleted document');
      
      // Update UI state only after both operations succeed
      setIdeas(prev => prev.filter(idea => idea.id !== id));
      setDeleteModal({ isOpen: false, id: '', title: '' });
    } catch (err) {
      console.error('Error in handleDeleteIdea:', err);
      setError('Failed to delete idea');
    }
  };

  const toggleIdeaPlayback = (id: string, url: string) => {
    const audioEl = audioPlayerRef.current;
    if (!audioEl) return;

    if (playingIdeaId === id) {
      audioEl.pause();
      setPlayingIdeaId(null);
      return;
    }

    audioEl.src = url;
    audioEl.play();
    setPlayingIdeaId(id);
    setAudioCurrentTime(0);
    setAudioDuration(0);
  };

  const handleIdeaAudioEnded = () => {
    setPlayingIdeaId(null);
    setAudioCurrentTime(0);
  };

  const formatTime = (secs: number) => {
    if (!secs || !isFinite(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleEditIdea = (id: string) => {
    setEditingId(id);
    setIsSidebarOpen(false); // Close sidebar when editing an idea
  };

  const handleInputChange = (field: 'title' | 'description' | 'audioUrl', value: string) => {
    if (editingId) {
      setIdeas(prev => prev.map(idea => 
        idea.id === editingId 
          ? { ...idea, [field]: value, updatedAt: new Date().toISOString() }
          : idea
      ));
    } else {
      setNewIdea(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleRemoveTag = (index: number) => {
    vibrate(HapticPatterns.LIGHT);
    if (editingId) {
      const idea = ideas.find(i => i.id === editingId);
      if (idea) {
        idea.tags.splice(index, 1);
      }
    } else {
      setNewIdea(prev => ({ ...prev, tags: prev.tags.filter((tag, i) => i !== index) }));
    }
  };

  const handleAddTag = () => {
    vibrate(HapticPatterns.LIGHT);
    if (!newTag.trim()) return;
    
    if (editingId) {
      const updatedIdea = ideas.find(i => i.id === editingId);
      if (updatedIdea) {
        const updatedTags = [...(updatedIdea.tags || []), newTag.toLowerCase().trim()];
        handleUpdateIdea(editingId, { ...updatedIdea, tags: updatedTags });
      }
    } else {
      setNewIdea(prev => ({
        ...prev,
        tags: [...(prev.tags || []), newTag.toLowerCase().trim()]
      }));
    }
    setNewTag('');
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && newTag.trim()) {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleAudioSave = async (url: string) => {
    if (!user) return;

    try {
      // Check free tier limit
      if (!isPremium && totalIdeasCreated >= FREE_TIER_LIMIT && !editingId) {
        setError(Capacitor.getPlatform() === 'ios' ? "You've reached the maximum allowed ideas for your account. To manage your account, visit www.showyourbits.com" : 'Free users can create up to 10 ideas total. Upgrade to Premium for unlimited ideas!');
        return;
      }

      const now = new Date();
      const formattedDate = now.toLocaleDateString('en-US', { 
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      });

      const ideaData = {
        userId: user.uid,
        title: newIdea.title || `Audio Recording - ${formattedDate}`,
        description: newIdea.description,
        tags: newIdea.tags || [],
        audioUrl: url,
        createdAt: now.toISOString(),
        updatedAt: now.toISOString()
      };

      console.log('Saving audio idea with data:', ideaData);

      const docRef = await addDoc(collection(db, 'ideas'), ideaData);
      console.log('Saved audio idea with ID:', docRef.id);
      
      // Increment total ideas created
      await incrementCreationCount(user.uid, 'ideas');
      setTotalIdeasCreated(prev => prev + 1);
      
      // Update local state
      setIdeas(prev => [{
        id: docRef.id,
        ...ideaData
      }, ...prev]);

      // Reset form
      setNewIdea({
        title: '',
        description: '',
        tags: [],
        audioUrl: ''
      });
      setError(null);
    } catch (err) {
      console.error('Error saving audio idea:', err);
      setError('Failed to save audio recording');
    }
  };

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-2xl font-bold mb-4">Please log in to access your Idea Bank</h1>
      </div>
    );
  }

  const filteredIdeas = ideas
    .filter(idea =>
      idea.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      idea.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      idea.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      return a.title.localeCompare(b.title);
    });

  // Audio of the idea currently open in the form (existing idea being edited, or the new-idea draft)
  const currentAudioUrl = editingId
    ? ideas.find(i => i.id === editingId)?.audioUrl
    : newIdea.audioUrl;

  return (
    <div className="relative min-h-screen flex flex-col">
      <div className="flex-1 flex flex-col md:flex-row">
        {/* Sidebar */}
        <div className={`
          fixed inset-y-0 left-0 z-50 w-full md:w-80 md:relative
          transform transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 bg-white md:bg-white border-r border-gray-200
        `}>
          {/* Search bar */}
          <div className="sticky top-0 z-10 bg-white p-4 border-b border-gray-200">
            <div className="flex items-center justify-between md:justify-start gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search ideas..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 pl-10"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="md:hidden p-2 hover:bg-gray-100 rounded-lg text-gray-900"
                aria-label="Open menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="mt-3">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'date' | 'title')}
                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="date">Sort by Date</option>
                <option value="title">Sort by Title</option>
              </select>
            </div>
          </div>

          {/* Ideas list */}
          <div className="saved-items-list p-4 overflow-y-auto h-full pb-32">
            {filteredIdeas.map((idea) => (
              <div 
                key={idea.id}
                onClick={() => handleEditIdea(idea.id)}
                className="w-full text-left p-4 hover:bg-gray-50 transition-colors border-b border-gray-100 text-gray-900"
              >
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-medium">{idea.title}</h3>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteModal({ isOpen: true, id: idea.id, title: idea.title });
                    }}
                    className="text-gray-400 hover:text-red-500 transition-colors p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-sm text-gray-600 mb-2 line-clamp-2">{idea.description}</p>
                {idea.audioUrl && (
                  <div className="mb-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => toggleIdeaPlayback(idea.id, idea.audioUrl!)}
                      className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                      aria-label={playingIdeaId === idea.id ? 'Pause recording' : 'Play recording'}
                    >
                      {playingIdeaId === idea.id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </button>
                    <input
                      type="range"
                      min={0}
                      max={playingIdeaId === idea.id && audioDuration ? audioDuration : 100}
                      value={playingIdeaId === idea.id ? audioCurrentTime : 0}
                      onChange={(e) => {
                        if (playingIdeaId === idea.id && audioPlayerRef.current) {
                          audioPlayerRef.current.currentTime = Number(e.target.value);
                        }
                      }}
                      className="flex-1 h-1 accent-orange-500 cursor-pointer"
                      aria-label="Seek recording"
                    />
                    <span className="flex-shrink-0 text-[10px] text-gray-400 tabular-nums w-9 text-right">
                      {formatTime(playingIdeaId === idea.id ? audioCurrentTime : 0)}
                    </span>
                  </div>
                )}
                <div className="flex flex-wrap gap-1 mb-1">
                  {idea.tags?.map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="text-xs text-gray-400">
                  {new Date(idea.createdAt).toLocaleDateString()}
                </div>
              </div>
            ))}
            <audio
              ref={audioPlayerRef}
              onEnded={handleIdeaAudioEnded}
              onTimeUpdate={(e) => setAudioCurrentTime(e.currentTarget.currentTime)}
              onLoadedMetadata={(e) => setAudioDuration(e.currentTarget.duration)}
              className="hidden"
            />
          </div>
        </div>

        {/* Overlay */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <div className="flex-1 w-full">
          <div className="max-w-4xl mx-auto p-0 md:p-8">
            {/* Mobile menu button */}
            <div className="md:hidden mb-4">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-900"
                aria-label="Open menu"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>

            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-orange-500 mb-2">Idea Bank</h1>
              <p className="text-gray-600">Have a premise, punchline, or idea you want to save for later? Store and organize your creative sparks here.</p>
            </div>

            {/* Usage counter for free users */}
            {!isPremium && (
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <span className="text-2xl font-bold text-orange-500">
                    {FREE_TIER_LIMIT - totalIdeasCreated}
                  </span>
                  <div className="flex flex-col">
                    <span className="text-orange-900 text-sm font-medium">
                      Ideas Remaining
                    </span>
                    <span className="text-xs text-orange-600">
                      Free users can create {FREE_TIER_LIMIT} ideas total
                    </span>
                  </div>
                </div>
                <UpgradeButton size="sm" className="whitespace-nowrap" />
              </div>
            )}

            {/* Content form */}
            <div className="bg-white rounded-lg shadow-sm p-8">
              {editingId && (
                <div className="flex justify-end mb-4">
                  <button
                    onClick={() => {
                      setEditingId('');
                      setNewIdea({
                        title: '',
                        description: '',
                        tags: [],
                        audioUrl: ''
                      });
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
                    aria-label="Cancel editing"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              )}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  placeholder="Enter idea title..."
                  value={editingId ? ideas.find(i => i.id === editingId)?.title || newIdea.title : newIdea.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500"
                />
              </div>

              {currentAudioUrl && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Recording</label>
                  <audio key={currentAudioUrl} controls src={currentAudioUrl} className="w-full" />
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  placeholder="Write your idea..."
                  value={editingId ? ideas.find(i => i.id === editingId)?.description || newIdea.description : newIdea.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500 min-h-[200px] resize-y"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(editingId ? ideas.find(i => i.id === editingId)?.tags : newIdea.tags)?.map((tag, index) => (
                    <div key={index} className="flex items-center gap-1 bg-orange-50 px-2 py-1 rounded-full text-sm">
                      <span className="text-orange-600">{tag}</span>
                      <button
                        onClick={() => {
                          if (editingId) {
                            const updatedIdea = ideas.find(i => i.id === editingId);
                            if (updatedIdea) {
                              const updatedTags = updatedIdea.tags.filter((_, i) => i !== index);
                              handleUpdateIdea(editingId, { ...updatedIdea, tags: updatedTags });
                            }
                          } else {
                            const updatedTags = [...newIdea.tags];
                            updatedTags.splice(index, 1);
                            setNewIdea({ ...newIdea, tags: updatedTags });
                          }
                        }}
                        className="text-orange-400 hover:text-orange-600"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col md:flex-row gap-2">
                  <input
                    type="text"
                    placeholder="Add a tag..."
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (newTag.trim() && !(editingId ? ideas.find(i => i.id === editingId)?.tags : newIdea.tags).includes(newTag.trim())) {
                          if (editingId) {
                            const updatedIdea = ideas.find(i => i.id === editingId);
                            if (updatedIdea) {
                              const updatedTags = [...(updatedIdea.tags || []), newTag.toLowerCase().trim()];
                              handleUpdateIdea(editingId, { ...updatedIdea, tags: updatedTags });
                            }
                          } else {
                            setNewIdea({
                              ...newIdea,
                              tags: [...(newIdea.tags || []), newTag.toLowerCase().trim()]
                            });
                          }
                          setNewTag('');
                        }
                      }
                    }}
                    className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-orange-500"
                  />
                  <button
                    onClick={() => {
                      if (newTag.trim() && !(editingId ? ideas.find(i => i.id === editingId)?.tags : newIdea.tags).includes(newTag.toLowerCase().trim())) {
                        if (editingId) {
                          const updatedIdea = ideas.find(i => i.id === editingId);
                          if (updatedIdea) {
                            const updatedTags = [...(updatedIdea.tags || []), newTag.toLowerCase().trim()];
                            handleUpdateIdea(editingId, { ...updatedIdea, tags: updatedTags });
                          }
                        } else {
                          setNewIdea({
                            ...newIdea,
                            tags: [...(newIdea.tags || []), newTag.toLowerCase().trim()]
                          });
                        }
                        setNewTag('');
                      }
                    }}
                    className={`w-full md:w-auto px-4 py-2 text-white rounded-lg transition-colors ${
                      newIdea.title.trim() ? 'bg-orange-500' : 'bg-[#FFBB80]'
                    }`}
                  >
                    Add Tag
                  </button>
                </div>
              </div>

              <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-4 pt-4">
                <div className="flex-1">
                  <AudioRecorder
                    onAudioSave={handleAudioSave}
                    isIdeasPage={true}
                  />
                </div>
                <button
                  onClick={editingId ?
                    () => {
                      const idea = ideas.find(i => i.id === editingId);
                      if (idea) {
                        handleUpdateIdea(editingId, {
                          title: idea.title,
                          description: idea.description,
                          tags: idea.tags,
                          updatedAt: new Date().toISOString()
                        });
                      }
                    }
                    : handleSaveIdea
                  }
                  className={`px-6 py-3 text-white rounded-lg transition-colors w-full md:w-auto ${
                    (editingId ? ideas.find(i => i.id === editingId)?.title : newIdea.title)?.trim() ? 'bg-orange-500' : 'bg-[#FFBB80]'
                  }`}
                >
                  {editingId ? 'Update' : 'Save Idea'}
                </button>
              </div>

              {error && (
                <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-lg">
                  {error}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <DeleteModal
        isOpen={deleteModal.isOpen}
        title={deleteModal.title}
        onClose={() => setDeleteModal({ isOpen: false, id: '', title: '' })}
        onConfirm={() => handleDeleteIdea(deleteModal.id)}
      />
    </div>
  );
}